import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../design/Button';
import TextField from '../design/TextField';
import LanguageToggle from '../components/LanguageToggle';
import { useSchoolAuth } from '../auth/SchoolAuthContext';
import { SchoolApiError } from '../utils/schoolApi';

type LoginTab = 'student' | 'teacher';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const { signInStudent, signInTeacher } = useSchoolAuth();
  const [tab, setTab] = useState<LoginTab>('student');
  const [classCode, setClassCode] = useState('');
  const [studentCode, setStudentCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const submitStudent = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErrorKey(null);
    try {
      await signInStudent(classCode, studentCode);
    } catch (err) {
      console.error('[school] Student sign-in failed', err);
      const code = err instanceof SchoolApiError ? err.code : 'server_error';
      setErrorKey(
        code === 'invalid_codes' || code === 'student_inactive'
          ? `school_error_${code}`
          : 'school_error_generic',
      );
    } finally {
      setBusy(false);
    }
  };

  const submitTeacher = async (): Promise<void> => {
    setBusy(true);
    setErrorKey(null);
    try {
      await signInTeacher();
    } catch (err) {
      console.error('[school] Teacher sign-in failed', err);
      setErrorKey('school_error_generic');
    } finally {
      setBusy(false);
    }
  };

  const tabClass = (active: boolean): string =>
    `flex-1 min-h-[44px] rounded-xl text-sm font-semibold transition-colors ${
      active
        ? 'bg-school-surface text-school-primary shadow-sm'
        : 'text-school-muted hover:text-school-ink'
    }`;

  return (
    // Scrolls itself: global CSS locks html/body/#root (see AppLayout).
    <div className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto bg-school-navy px-4 py-10">
      <div className="my-auto flex w-full flex-col items-center">
        <div className="mb-6 flex flex-col items-center text-white">
          <img src="/logo.png" alt="" className="mb-3 h-20 w-20 rounded-2xl" />
          <h1 className="text-3xl font-extrabold">{t('school_app_name')}</h1>
          <p className="text-sm text-white/70">{t('school_app_tagline')}</p>
        </div>

        <div className="w-full max-w-md rounded-3xl bg-school-surface p-6 shadow-xl sm:p-8">
          <div className="mb-6 flex gap-1 rounded-2xl bg-school-canvas p-1" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'student'}
              className={tabClass(tab === 'student')}
              onClick={(): void => setTab('student')}
            >
              {t('school_login_tab_student')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'teacher'}
              className={tabClass(tab === 'teacher')}
              onClick={(): void => setTab('teacher')}
            >
              {t('school_login_tab_teacher')}
            </button>
          </div>

          {tab === 'student' ? (
            <form onSubmit={submitStudent} className="flex flex-col gap-4">
              <TextField
                label={t('school_class_code')}
                value={classCode}
                onChange={(v: string): void => setClassCode(v.toUpperCase())}
                placeholder="ABC123"
                maxLength={8}
                dir="ltr"
              />
              <TextField
                label={t('school_personal_code')}
                value={studentCode}
                onChange={(v: string): void => setStudentCode(v.replace(/\D/g, ''))}
                placeholder="1234"
                inputMode="numeric"
                maxLength={4}
                dir="ltr"
              />
              <Button
                type="submit"
                size="lg"
                fullWidth
                loading={busy}
                disabled={!classCode.trim() || studentCode.length !== 4}
              >
                {t('school_login_enter')}
              </Button>
            </form>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-school-muted">{t('school_login_teacher_hint')}</p>
              <Button size="lg" fullWidth loading={busy} onClick={submitTeacher}>
                {t('school_login_google')}
              </Button>
            </div>
          )}

          {errorKey && (
            <p
              className="mt-4 rounded-xl bg-school-danger-soft p-3 text-center text-sm text-school-danger"
              role="alert"
            >
              {t(errorKey)}
            </p>
          )}
        </div>

        <LanguageToggle className="mt-6 text-white/80" />
      </div>
    </div>
  );
};

export default LoginPage;
